import { useEffect, useState } from "react";

import { useParams } from "react-router-dom";

import DashboardLayout from "../../../components/layout/DashboardLayout";

import { useRepairQuotationViewModel } from "../../../viewmodels/operation/repairquotation/useRepairQuotationViewModel";

import ReportHeader from "../../../components/reports/ReportHeader";

import ReportFooter from "../../../components/reports/ReportFooter";

const ViewRepairQuotation = () => {
  const { id } = useParams();

  const { getRepairQuotationById } = useRepairQuotationViewModel();

  const [quotation, setQuotation] = useState<any>(null);

  // ================= DATE =================
  const formatDate = (date: string) => {
    if (!date) return "";

    return new Date(date).toLocaleDateString("en-GB");
  };

  // ================= LOAD =================
  const loadData = async () => {
    if (!id) return;

    const data = await getRepairQuotationById(id);

    if (data) {
      setQuotation(data);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  // ================= PRINT =================
  const handlePrint = () => {
    window.print();
  };

  if (!quotation) {
    return (
      <DashboardLayout>
        <div className="p-10">Loading...</div>
      </DashboardLayout>
    );
  }

  // ================= EMPTY ROWS =================
  const emptyRows = Math.max(0, 8 - (quotation?.lineItems?.length || 0));

  return (
    <DashboardLayout>
      {/* PRINT STYLE */}
      <style>
        {`

            @page {

              size: A4 portrait;
              margin: 4mm;

            }

            @media print {

              html,
              body {

                width: 210mm;
                height: 297mm;

                background: white !important;

                overflow: hidden !important;

              }

              body * {

                visibility: hidden;

              }

              .print-area,
              .print-area * {

                visibility: visible;

              }

              .print-area {

                position: absolute;

                left: 0;
                top: 0;

                width: 100%;

                background: white;

              }

              .print-hide {

                display: none !important;

              }

            }

          `}
      </style>

      <div className="bg-slate-100 min-h-screen p-4 print:bg-white print:p-0">
        {/* PRINT BUTTON */}
        <div className="flex justify-end mb-4 print-hide">
          <button
            onClick={handlePrint}
            className="bg-primary text-white px-5 py-3 rounded-xl"
          >
            Print
          </button>
        </div>

        {/* PAPER */}
        <div className="print-area w-full max-w-[980px] mx-auto bg-white px-3 py-2 text-[10px] leading-[1] min-h-[1120px] flex flex-col">
          {/* HEADER */}
          <ReportHeader />

          {/* TITLE */}
          <div className="flex justify-between items-center mb-5">
            <div className="font-bold  text-[15px] underline flex-1 text-center">
              REPAIR QUOTATION
            </div>

            <div className="border border-black px-4 py-1 text-[16px] font-bold ml-4">
              {quotation.no}
            </div>
          </div>

          {/* DATE */}
          <div className="font-bold  text-[12px] mb-5">
            DATE: {formatDate(quotation.date)}
          </div>

          {/* DETAILS */}
          <div className="grid grid-cols-2 gap-8 mb-6">
            {/* CUSTOMER */}
            <div>
              <div className="font-bold  text-[12px] mb-2">
                CUSTOMER DETAILS:
              </div>

              <div className="border border-black min-h-[105px] p-3 text-[12px]">
                <div>{quotation.customerName}</div>

                <div className="mt-2 whitespace-pre-wrap">
                  {quotation.customerAddress}
                </div>

                <div className="mt-3">{quotation.contactPerson}</div>

                <div>{quotation.contactPhoneNumber}</div>

                <div>{quotation.customerEmail}</div>
              </div>
            </div>

            {/* SCALE */}
            <div>
              <div className="font-bold  text-[12px] mb-2">SCALE DETAILS:</div>

              <div className="border border-black text-[12px]">
                <div className="border-b border-black p-2">
                  MAKE: {quotation.make}
                </div>

                <div className="border-b border-black p-2">
                  MODEL: {quotation.model}
                </div>

                <div className="border-b border-black p-2">
                  CAP: {quotation.capacity}
                </div>

                <div className="p-2">SER NO: {quotation.serialNumber}</div>
              </div>
            </div>
          </div>

          {/* FAULT */}
          <div className="mb-6">
            <div className="font-bold  text-[12px] mb-2">
              FAULT DESCRIPTION:
            </div>

            <div className="border border-black min-h-[55px] p-3 text-[12px] whitespace-pre-wrap">
              {quotation.faultDescription}
            </div>
          </div>

          {/* TABLE */}
          <table className="w-full border border-collapse text-[12px] mb-4">
            <tbody>
              {quotation.lineItems?.map((item: any, index: number) => (
                <tr key={index}>
                  <td className="border border-black p-2 h-[26px]">
                    {item.description}
                  </td>

                  <td className="border border-black p-2 w-[180px] text-right">
                    ${Number(item.amount || 0).toFixed(2)}
                  </td>
                </tr>
              ))}

              {Array.from({
                length: emptyRows,
              }).map((_, index) => (
                <tr key={`empty-${index}`}>
                  <td className="border border-black h-[26px]"></td>

                  <td className="border border-black"></td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* TOTALS */}
          <div className="flex justify-end mb-6">
            <div className="w-[320px] text-[12px]">
              <div className="flex justify-between border border-black border-b-0 p-2">
                <span>Sub-Total</span>

                <span>${Number(quotation.subTotal || 0).toFixed(2)}</span>
              </div>

              <div className="flex justify-between border border-black border-b-0 p-2">
                <span>GST</span>

                <span>${Number(quotation.gst || 0).toFixed(2)}</span>
              </div>

              <div className="flex justify-between border border-black p-2 font-bold">
                <span>Total (inc GST)</span>

                <span>${Number(quotation.total || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* APPROVAL */}
          <div className="text-[12px] font-bold  mb-8">
            PLEASE COMPLETE AND EMAIL TO: sales@universalweighing.com.au
          </div>

          <div className="text-[12px] font-bold  mb-4">
            I APPROVE ABOVE MENTIONED REPAIR:
          </div>

          <div className="grid grid-cols-2 gap-8 text-[12px] font-bold mb-5">
            <div>NAME: ________________________</div>

            <div>SIGNATURE: ________________________</div>
          </div>

          <div className="grid grid-cols-2 gap-8 text-[12px] font-bold mb-10">
            <div>ORDER NO: ________________________</div>

            <div>DATE: ________________________</div>
          </div>

          {/* FOOTER */}
          <ReportFooter />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ViewRepairQuotation;
